import { useOutsideClick } from '@/hooks';
import { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { twJoin, twMerge } from 'tailwind-merge';
import { CrossIcon } from '../Icons';

export type ModalProps = PropsWithChildren<{
  isOpen: boolean;
  className?: string;
  modalClassName?: string;
  title?: string | ReactNode;
  handleClose: () => void;
  allowClickOutsideToClose?: boolean;
  hideCloseButton?: boolean;
  banner?: ReactNode;
}>;

export const Modal = ({
  isOpen,
  children,
  title,
  className,
  modalClassName,
  handleClose,
  allowClickOutsideToClose = true,
  hideCloseButton = false,
  banner,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useOutsideClick(modalRef, handleClose, allowClickOutsideToClose);

  // Prevent scrolling on the screen behind the Modal
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
  }, [isOpen]);

  return (
    isOpen &&
    createPortal(
      <>
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div
            className={twMerge(
              'absolute z-20 m-auto flex h-fit max-h-[80%] w-full max-w-screen-2xl flex-col overflow-auto rounded-xl bg-white',
              '[scrollbar-color:#c1c1c1_transparent]',
              '[&::-webkit-scrollbar-track]:rounded-xl',
              '[&::-webkit-scrollbar-track]:bg-transparent',
              modalClassName,
            )}
            ref={modalRef}
          >
            {banner}
            <div className={twMerge('p-5', className)}>
              {(title || !hideCloseButton) && (
                <div className="flex justify-between">
                  <div className="text-xl font-bold">{title}</div>
                  <CrossIcon
                    className={twJoin(
                      'size-6 cursor-pointer',
                      hideCloseButton && 'hidden',
                    )}
                    onClick={handleClose}
                  />
                </div>
              )}
              {children}
            </div>
          </div>
        </div>
      </>,
      document.body,
    )
  );
};
